/**
 * One vendor invoice, in full.
 *
 * Reached by clicking the invoice number on a payable. Shows the bill exactly
 * as it was keyed, the scans attached to it, which payables it has been matched
 * against, and what has been paid — plus the two ways back out of a mistake:
 * unmatch it, or delete it outright.
 *
 * Deleting is deliberately hard to do by accident. An invoice with a payment
 * against it can never be deleted, and one that is matched has to be unmatched
 * first, so the payable's figures are recalculated in the open.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, FileMinus, Link2Off, Paperclip, Trash2 } from 'lucide-react'
import {
  Alert, Card, Confirm, EmptyState, Field, Input, Loading, Modal, StatusPill, Textarea, useToast,
} from '@/components/ui'
import {
  AttachmentCollector, AttachmentList, FileChips, VendorPapersCard, queueAttachments,
  useAttachmentsFor, type PendingAttachment,
} from '@/components/docs/shared'
import { useAuth } from '@/lib/auth'
import { DocumentDate } from '@/components/docs/DocumentDate'
import { supabase } from '@/lib/supabase'
import { can } from '@/lib/domain'
import { ddmmyyyy, dateTime, rupees, localIso } from '@/lib/format'

type Any = Record<string, any>

export function InvoiceDetail() {
  const { id = '' } = useParams()
  const { profile } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  // Booking and matching: Accounts and Procurement both.
  const maySettle = can(profile!.role, 'recordInvoice')
  // Destroying: Procurement only. Deleting an invoice erases the claim
  // against a vendor and deleting a credit note puts money back on what he
  // is owed — neither belongs in the same hands as recording the payment.
  const mayDestroy = can(profile!.role, 'managePayables')

  const [invoice, setInvoice] = useState<Any | null>(null)
  const [vendor, setVendor] = useState<Any | null>(null)
  const [recons, setRecons] = useState<Any[]>([])
  const [payments, setPayments] = useState<Any[]>([])
  const [credits, setCredits] = useState<Any[]>([])
  const [creditOpen, setCreditOpen] = useState(false)
  const [deletingCredit, setDeletingCredit] = useState<Any | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [unmatching, setUnmatching] = useState<Any | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data: inv, error: e } = await supabase
        .from('vendor_invoices').select('*').eq('id', id).maybeSingle()
      if (e) throw new Error(e.message)
      if (!inv) throw new Error(`Invoice ${id} was not found, or you do not have access to it.`)
      setInvoice(inv)

      const { data: v } = await supabase.from('vendors')
        .select('id, company_name, gstin, pan, phone, email').eq('id', inv.vendor_id).maybeSingle()
      setVendor(v)

      const { data: rc } = await supabase.from('invoice_reconciliations')
        .select('*, vendor_payables(id, payable_amount, status, grn_id, site_id)')
        .eq('invoice_id', id)
      setRecons(rc ?? [])

      const { data: pm } = await supabase.from('vendor_payments')
        .select('*').eq('invoice_id', id).order('payment_date', { ascending: false })
      setPayments(pm ?? [])

      const { data: cn } = await supabase.from('vendor_credit_notes')
        .select('*').eq('invoice_id', id).order('credit_date', { ascending: false })
      setCredits(cn ?? [])

      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the invoice')
    } finally { setLoading(false) }
  }, [id])

  useEffect(() => { void load() }, [load])

  const creditFiles = useAttachmentsFor('CREDIT_NOTE', credits.map((c) => c.id))
  const paymentFiles = useAttachmentsFor('PAYMENT', payments.map((p) => p.id))

  if (loading) return <Loading label="Loading invoice…" />
  if (error || !invoice) return <Alert tone="error">{error}</Alert>

  const paid = payments.reduce((a, p) => a + Number(p.amount || 0), 0)
  const credited = credits.reduce((a, c) => a + Number(c.total_amount || 0), 0)
  const matched = Number(invoice.matched_amount || 0)
  const total = Number(invoice.total_amount || 0)
  // What we will actually pay him: his bill, less anything he has credited back.
  const net = total - credited
  const stillDue = net - paid
  const canDelete = mayDestroy && payments.length === 0 && recons.length === 0 && credits.length === 0

  async function unmatch(payableId: string) {
    setBusy(true)
    try {
      const { error: e } = await supabase.rpc('rpc_unmatch_invoice', {
        p_invoice_id: id, p_payable_id: payableId,
      })
      if (e) throw new Error(e.message)
      toast.success(`Unmatched from ${payableId}. That payable is open again.`)
      await load()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not unmatch the invoice')
    } finally { setBusy(false); setUnmatching(null) }
  }

  async function remove() {
    setBusy(true)
    try {
      // Take the files out of the bucket first — once the rows are gone we no
      // longer know where they live.
      const { data: files } = await supabase.from('document_attachments')
        .select('file_path').eq('doc_type', 'INVOICE').eq('doc_id', id)
      const paths = (files ?? []).map((f: Any) => f.file_path).filter(Boolean)
      if (paths.length) await supabase.storage.from('doc-attachments').remove(paths)

      const { error: e } = await supabase.rpc('rpc_delete_vendor_invoice', { p_invoice_id: id })
      if (e) throw new Error(e.message)

      toast.success(`Invoice ${invoice!.invoice_no} deleted. Enter it again with the correct details.`)
      navigate('/payables')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not delete the invoice')
    } finally { setBusy(false); setConfirmDelete(false) }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/payables" className="btn-ghost btn-sm"><ArrowLeft className="h-4 w-4" /> All payables</Link>
          <h1 className="font-mono text-base font-bold text-slate-800">{invoice.id}</h1>
          <StatusPill status={invoice.status ?? 'OPEN'} />
        </div>
        {mayDestroy && (
          <button className="btn-danger btn-sm" onClick={() => setConfirmDelete(true)}
                  disabled={busy || !canDelete}
                  title={canDelete
                    ? 'Delete this invoice and its scans'
                    : payments.length
                      ? 'Cannot delete — a payment has already been recorded against this invoice'
                      : 'Cannot delete — unmatch it from the payable(s) below first'}>
            <Trash2 className="h-4 w-4" /> Delete invoice
          </button>
        )}
      </div>

      {maySettle && !canDelete && (
        <Alert tone="info" title="This invoice cannot be deleted as it stands">
          {payments.length > 0
            ? <>A payment of <strong>{rupees(paid)}</strong> has been recorded against it. Money has already
               moved, so correcting this is an accounting entry, not a delete.</>
            : <>It is matched to {recons.length} payable(s). Unmatch it below, then delete it — that way the
               payable&rsquo;s figures are recalculated in the open.</>}
        </Alert>
      )}

      {/* ------------------------------------------------------- the bill */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="The vendor's bill, as keyed" className="lg:col-span-2">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm sm:grid-cols-3">
            <D label="Invoice number" value={invoice.invoice_no} />
            <D label="Invoice date" value={ddmmyyyy(invoice.invoice_date)} />
            <D label="Received on" value={invoice.received_date ? ddmmyyyy(invoice.received_date) : '—'} />
            <D label="Vendor" value={vendor?.company_name ?? invoice.vendor_id} />
            <D label="GSTIN" value={vendor?.gstin ?? '—'} />
            <D label="Against purchase order" value={invoice.po_id ?? '—'}
               to={invoice.po_id ? `/pos/${invoice.po_id}` : undefined} />
          </dl>
          {invoice.remarks && (
            <p className="mt-4 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">{invoice.remarks}</p>
          )}
        </Card>

        <Card title="Amounts">
          <dl className="space-y-2 text-sm">
            <Row label="Taxable" value={invoice.taxable_amount} />
            <Row label="GST" value={invoice.gst_amount} />
            <Row label="Invoice total" value={total} strong />
            {credited > 0 && (
              <Row label="Less credit notes" value={-credited} tone="good" />
            )}
            {credited > 0 && <Row label="Net payable" value={net} strong />}
            <hr className="my-2" />
            <Row label="Matched to payables" value={matched} tone={matched > 0 ? 'good' : undefined} />
            <Row label="Not yet matched" value={Math.max(0, total - matched)}
                 tone={total - matched > 0.01 ? 'warn' : 'good'} />
            <hr className="my-2" />
            <Row label="Paid" value={paid} tone={paid > 0 ? 'good' : undefined} />
            <Row label={stillDue < -0.01 ? 'Recoverable from vendor' : 'Still to pay'}
                 value={Math.abs(stillDue)} strong
                 tone={stillDue > 0.01 ? 'warn' : stillDue < -0.01 ? 'warn' : 'good'} />
          </dl>
        </Card>
      </div>

      <AttachmentList docType="INVOICE" docId={invoice.id}
                      title="Invoice scans"
                      subtitle="The bill as the vendor sent it" />

      {/* The vendor's OWN papers, as opposed to this bill's. The GST
          certificate is what justifies the input credit claimed below, so it
          belongs on this screen. Draws nothing for a role that may not read
          them. */}
      <VendorPapersCard vendorId={invoice.vendor_id}
                        vendorName={vendor?.company_name}
                        subtitle="GST certificate, PAN and bank mandate on file — check the certificate before claiming the input credit" />

      {/* -------------------------------------------------- credit notes */}
      <Card
        title="Credit notes against this invoice"
        subtitle="What the vendor has agreed to knock off — over-billing, a rate correction, material sent back"
        actions={maySettle && (
          <button className="btn-secondary btn-sm" onClick={() => setCreditOpen(true)} disabled={busy}>
            <FileMinus className="h-4 w-4" />
            {total - matched > 0.01 ? 'Record the credit note we claimed' : 'Record credit note'}
          </button>
        )}
      >
        {credits.length === 0 && total - matched > 0.01 && (
          <Alert tone="warning" title={`You are claiming ${rupees(total - matched)} back from this vendor`}>
            He billed <strong>{rupees(total)}</strong>; only <strong>{rupees(matched)}</strong> was matched
            as genuinely owed. Chase him for a credit note of{' '}
            <strong>{rupees(total - matched)}</strong>, then press the button above — the amounts are
            already worked out for you, you only type his credit note number and attach the document.
          </Alert>
        )}
        {credits.length === 0 && total - matched <= 0.01 && (
          <p className="text-sm text-slate-500">
            None, and none is claimed — the whole invoice was accepted as owed.
          </p>
        )}

        {credits.length > 0 && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr><th>Credit note</th><th>His number</th><th>Date</th>
                    <th className="num">Taxable</th><th className="num">GST</th><th className="num">Total</th>
                    <th>Document</th><th>Reason</th><th /></tr>
              </thead>
              <tbody>
                {credits.map((c) => (
                  <tr key={c.id}>
                    <td className="font-mono text-xs">{c.id}</td>
                    <td className="text-sm font-medium">{c.credit_no}</td>
                    <td className="text-xs">{ddmmyyyy(c.credit_date)}</td>
                    <td className="num text-xs">{rupees(c.taxable_amount)}</td>
                    <td className="num text-xs">{rupees(c.gst_amount)}</td>
                    <td className="num text-sm font-semibold text-emerald-700">{rupees(c.total_amount)}</td>
                    <td><FileChips files={creditFiles[c.id]} /></td>
                    <td className="max-w-[260px] truncate text-xs text-slate-500" title={c.reason}>{c.reason}</td>
                    <td className="text-right">
                      {mayDestroy && (
                        <button className="btn-ghost btn-sm" disabled={busy}
                                onClick={() => setDeletingCredit(c)}
                                aria-label="Delete credit note">
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-3 text-right text-sm">
              Credited in total: <strong className="text-emerald-700">{rupees(credited)}</strong>
              {' · '}net payable <strong>{rupees(net)}</strong>
            </p>
          </div>
        )}
      </Card>

      {/* ------------------------------------------------------ matched to */}
      <Card title="Matched against these payables"
            subtitle="One invoice can cover several goods receipts; each match is recorded separately">
        {recons.length === 0 && (
          <EmptyState icon={<Paperclip className="h-8 w-8" />} title="Not matched to anything yet"
                      message="Open the payable this bill relates to and use Match to this payable." />
        )}
        {recons.length > 0 && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr><th>Payable</th><th>Goods receipt</th><th className="num">Payable amount</th>
                    <th className="num">Matched</th><th className="num">Variance</th>
                    <th>Variance reason</th><th>Matched on</th><th>Status</th><th /></tr>
              </thead>
              <tbody>
                {recons.map((r) => {
                  const pay = r.vendor_payables ?? {}
                  const variance = Number(r.variance || 0)
                  return (
                    <tr key={r.id}>
                      <td>
                        <Link to={`/payables/${r.payable_id}`}
                              className="font-mono text-xs text-oval-700 hover:underline">
                          {r.payable_id}
                        </Link>
                      </td>
                      <td className="font-mono text-xs text-slate-500">{pay.grn_id ?? '—'}</td>
                      <td className="num text-xs">{rupees(pay.payable_amount ?? 0)}</td>
                      <td className="num text-xs font-semibold">{rupees(r.matched_amount)}</td>
                      <td className={`num text-xs ${Math.abs(variance) > 0.01 ? 'font-semibold text-red-600' : 'text-emerald-700'}`}>
                        {Math.abs(variance) > 0.01 ? rupees(variance) : 'nil'}
                      </td>
                      <td className="max-w-[220px] truncate text-xs text-slate-500">{r.variance_reason ?? '—'}</td>
                      <td className="text-xs">{dateTime(r.reconciled_at)}</td>
                      <td><StatusPill status={pay.status ?? 'OPEN'} /></td>
                      <td className="text-right">
                        {maySettle && payments.length === 0 && (
                          <button className="btn-secondary btn-sm" disabled={busy}
                                  onClick={() => setUnmatching(r)}>
                            <Link2Off className="h-3 w-3" /> Unmatch
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* -------------------------------------------------------- payments */}
      <Card title="Payments against this invoice"
            subtitle="Recorded after the accounts team has actually paid">
        {payments.length === 0 && <p className="text-sm text-slate-500">Nothing paid yet.</p>}
        {payments.length > 0 && (
          <div className="table-wrap">
            <table className="tbl">
              <thead>
                <tr><th>Payment</th><th>Date</th><th>Mode</th><th>Reference</th>
                    <th className="num">Amount</th><th>Proof</th><th>Remarks</th></tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id}>
                    <td className="font-mono text-xs">{p.id}</td>
                    <td className="text-xs">{ddmmyyyy(p.payment_date)}</td>
                    <td className="text-xs">{p.mode}</td>
                    <td className="font-mono text-xs">{p.reference_no ?? '—'}</td>
                    <td className="num text-sm font-semibold text-emerald-700">{rupees(p.amount)}</td>
                    <td><FileChips files={paymentFiles[p.id]} /></td>
                    <td className="max-w-[200px] truncate text-xs text-slate-500">{p.remarks ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Confirm
        open={!!unmatching}
        title={unmatching ? `Unmatch from ${unmatching.payable_id}?` : ''}
        message={<>
          The match of <strong>{unmatching ? rupees(unmatching.matched_amount) : ''}</strong> is removed and
          that payable goes back to awaiting an invoice. The invoice itself stays on file. Nothing is paid,
          so no money is affected.
        </>}
        confirmLabel="Unmatch"
        tone="warning"
        onCancel={() => setUnmatching(null)}
        onConfirm={async () => { if (unmatching) await unmatch(unmatching.payable_id) }}
      />

      {creditOpen && (
        <CreditNoteModal
          invoice={invoice} maxCredit={total - credited} claim={Math.max(0, total - matched - credited)}
          payables={recons.map((r) => r.payable_id)}
          busy={busy} setBusy={setBusy}
          onClose={() => setCreditOpen(false)}
          onDone={async () => { setCreditOpen(false); await load() }}
          toast={toast}
        />
      )}

      <Confirm
        open={!!deletingCredit}
        title={deletingCredit ? `Delete credit note ${deletingCredit.credit_no}?` : ''}
        message="The credit is removed and the full invoice becomes payable again. Do this only if it was recorded in error."
        confirmLabel="Delete"
        tone="danger"
        onCancel={() => setDeletingCredit(null)}
        onConfirm={async () => {
          if (!deletingCredit) return
          setBusy(true)
          try {
            const { error: e } = await supabase.rpc('rpc_delete_credit_note',
              { p_credit_note_id: deletingCredit.id })
            if (e) throw new Error(e.message)
            toast.success('Credit note deleted.')
            await load()
          } catch (e) {
            toast.error(e instanceof Error ? e.message : 'Could not delete the credit note')
          } finally { setBusy(false); setDeletingCredit(null) }
        }}
      />

      <Confirm
        open={confirmDelete}
        title={`Delete invoice ${invoice.invoice_no}?`}
        message={<>
          The invoice and its scans are removed permanently. Nothing else is touched — the goods receipt and
          the payable stay exactly as they are, and the payable simply goes back to awaiting a bill.
          Use this when the number or the amount was keyed wrongly, then enter it again.
        </>}
        confirmLabel="Delete permanently"
        tone="danger"
        requireReason={false}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={remove}
      />
    </div>
  )
}

const D = ({ label, value, to }: { label: string; value: string; to?: string }) => (
  <div>
    <dt className="text-xs font-semibold text-slate-500">{label}</dt>
    <dd className="mt-0.5 break-words text-slate-800">
      {to ? <Link to={to} className="font-mono text-sm text-oval-700 hover:underline">{value}</Link> : value}
    </dd>
  </div>
)

const Row = ({ label, value, strong, tone }: {
  label: string; value: number | string; strong?: boolean; tone?: 'good' | 'warn'
}) => (
  <div className="flex items-center justify-between">
    <dt className="text-slate-600">{label}</dt>
    <dd className={[
      strong ? 'font-bold' : '',
      tone === 'good' ? 'text-emerald-700' : tone === 'warn' ? 'text-amber-700' : 'text-slate-800',
    ].join(' ')}>{rupees(Number(value) || 0)}</dd>
  </div>
)

// ---------------------------------------------------------------------------
function CreditNoteModal({ invoice, maxCredit, claim, payables, busy, setBusy, onClose, onDone, toast }: Any) {
  // The claim is already known: it is whatever we refused to match. Split it
  // into taxable and GST in the same proportion as the invoice itself, so the
  // figures agree with his books as well as ours.
  const invTotal = Number(invoice.total_amount || 0)
  const claimAmt = Number(claim || 0)
  const suggestedTaxable = invTotal > 0
    ? Math.round(claimAmt * (Number(invoice.taxable_amount || 0) / invTotal) * 100) / 100 : 0
  const suggestedGst = Math.round((claimAmt - suggestedTaxable) * 100) / 100

  const [creditNo, setCreditNo] = useState('')
  const [creditDate, setCreditDate] = useState(localIso())
  const [taxable, setTaxable] = useState(claimAmt > 0.01 ? String(suggestedTaxable) : '')
  const [gst, setGst] = useState(claimAmt > 0.01 ? String(suggestedGst) : '')
  const [reason, setReason] = useState(claimAmt > 0.01
    ? `Over-billed on invoice ${invoice.invoice_no}. Only ${rupees(Number(invoice.matched_amount || 0))} was accepted as owed against ${payables?.length === 1 ? payables[0] : `${payables?.length ?? 0} goods receipt(s)`}; the balance relates to material rejected at site. Credit note received and agreed.`
    : '')
  const [files, setFiles] = useState<PendingAttachment[]>([])

  const total = (Number(taxable) || 0) + (Number(gst) || 0)
  const overshoot = total > maxCredit + 0.01

  async function save() {
    if (!creditNo.trim()) { toast.error("Enter the vendor's credit note number."); return }
    if (!(total > 0)) { toast.error('The credit note must be for more than zero.'); return }
    if (!reason.trim()) { toast.error('Say why he issued it — an auditor will read this.'); return }
    setBusy(true)
    try {
      const { data, error } = await supabase.rpc('rpc_record_credit_note', {
        p_invoice_id: invoice.id,
        p_credit_no: creditNo.trim(),
        p_credit_date: creditDate,
        p_taxable_amount: Number(taxable) || 0,
        p_gst_amount: Number(gst) || 0,
        p_reason: reason.trim(),
      })
      if (error) throw new Error(error.message)
      if (files.length) await queueAttachments('CREDIT_NOTE', data as string, files)
      toast.success(`Credit note ${data} recorded. The amount payable has come down.`)
      await onDone()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not record the credit note')
    } finally { setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} wide
           title="Record the vendor's credit note"
           subtitle={`Against invoice ${invoice.invoice_no} · ${rupees(invoice.total_amount)}`}
           footer={<>
             <button className="btn-secondary" onClick={onClose} disabled={busy}>Cancel</button>
             <button className="btn-primary" onClick={save} disabled={busy || overshoot}>
               Record credit note
             </button>
           </>}>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="His credit note number" required hint="Exactly as printed on his document">
          <Input value={creditNo} onChange={(e) => setCreditNo(e.target.value)} autoFocus />
        </Field>
        <DocumentDate label="Credit note date" value={creditDate} onChange={setCreditDate}
                      hint="The date printed on the vendor's credit note." />
        <Field label="Taxable amount (₹)" required>
          <Input type="number" step="any" value={taxable} onChange={(e) => setTaxable(e.target.value)} />
        </Field>
        <Field label="GST amount (₹)">
          <Input type="number" step="any" value={gst} onChange={(e) => setGst(e.target.value)} />
        </Field>
        <Field label="Why did he issue it?" required className="sm:col-span-2"
               hint={claimAmt > 0.01
                 ? 'Drafted from what you refused to match. Correct it to say what actually happened.'
                 : 'Be specific. This is the justification for paying him less than he billed.'}>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Billed 2,100 CFT; 100 CFT rejected at Khubal on 7 Aug for excess dust. Credit agreed by telephone with Mr Saha." />
        </Field>
      </div>

      <div className="mt-4 rounded-lg border border-slate-200 p-3 text-sm">
        {claimAmt > 0.01 && (
          <div className="mb-1 flex justify-between text-xs text-oval-800">
            <span>What we claimed from him</span><strong>{rupees(claimAmt)}</strong>
          </div>
        )}
        <div className="flex justify-between"><span>Credit note total</span><strong>{rupees(total)}</strong></div>
        <div className="flex justify-between text-xs text-slate-500">
          <span>Most that can still be credited on this invoice</span><span>{rupees(maxCredit)}</span>
        </div>
        {claimAmt > 0.01 && Math.abs(total - claimAmt) > 0.01 && (
          <p className="mt-2 text-xs font-semibold text-amber-700">
            He has credited {total > claimAmt ? 'more' : 'less'} than we claimed.
            {total < claimAmt && ` ${rupees(claimAmt - total)} of the claim remains outstanding.`}
          </p>
        )}
        {overshoot && (
          <p className="mt-2 text-xs font-semibold text-red-600">
            That is more than he ever billed. Check the figures.
          </p>
        )}
      </div>

      <div className="mt-4">
        <AttachmentCollector docType="CREDIT_NOTE" value={files} onChange={setFiles}
                             hint="Attach his credit note document. Without it this is your word against his." />
      </div>

      <div className="mt-4">
        <Alert tone="info">
          Recording this reduces what we owe him. If the invoice has already been paid in full, the
          payable will show a <strong>recoverable</strong> balance — money to take off his next bill.
        </Alert>
      </div>
    </Modal>
  )
}
